(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-sf-paid-landing]';

  class SplitflaskPaidLanding {
    constructor(root) {
      if (!root || root.dataset.sfPaidInitialized === 'true') return;
      root.dataset.sfPaidInitialized = 'true';

      this.root = root;
      this.currency = root.dataset.currency || 'USD';
      this.baseCtaLabel = root.dataset.ctaLabel || 'Add to cart';
      this.variants = this.readVariants();
      this.variantButtons = Array.from(root.querySelectorAll('[data-sf-variant]'));
      this.bundleButtons = Array.from(root.querySelectorAll('[data-sf-bundle]'));
      this.mixSelects = Array.from(root.querySelectorAll('[data-sf-mix-select]'));
      this.mixSlots = Array.from(root.querySelectorAll('[data-sf-mix-slot]'));
      this.mixPanel = root.querySelector('[data-sf-mix-panel]');
      this.addButton = root.querySelector('[data-sf-add]');
      this.stickyButton = root.querySelector('[data-sf-sticky-add]');
      this.sticky = root.querySelector('[data-sf-sticky]');
      this.status = root.querySelector('[data-sf-paid-status]');
      this.mainImage = root.querySelector('.sf-paid-gallery__main-image');
      this.mediaButtons = Array.from(root.querySelectorAll('[data-sf-media-thumb]'));
      this.selectedVariantId = Number(
        this.variantButtons.find((button) => button.classList.contains('is-selected'))?.dataset.variantId ||
        this.variants.find((variant) => variant.available)?.id ||
        0
      );
      this.quantity = 1;
      this.discount = 0;
      this.submitting = false;

      this.disableLandingDistractions();
      this.bindEvents();
      this.syncMixSelections(true);
      this.updateOffer();
      this.setupStickyButton();
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector('[data-sf-paid-variants]')?.textContent || '[]');
      } catch (error) {
        console.warn('Splitflask paid landing: variant data could not be read.', error);
        return [];
      }
    }

    disableLandingDistractions() {
      if (this.root.dataset.suppressPopups !== 'true') return;

      const suppressTimerPopups = () => {
        document.querySelectorAll('[data-sf-timer-popup]').forEach((popup) => {
          popup.dataset.enabled = 'false';
          popup.dataset.previewEnabled = 'false';

          const timer = popup.sfTimerInstance;
          if (timer?.config) {
            timer.config.enabled = false;
            timer.config.previewEnabled = false;
            timer.stopTriggerMonitoring?.();
            if (timer.opened || popup.classList.contains('is-open')) timer.close?.();
          }

          if (popup.classList.contains('is-open')) popup.classList.remove('is-open');
          if (popup.getAttribute('aria-hidden') !== 'true') popup.setAttribute('aria-hidden', 'true');
        });

        if (document.body.classList.contains('sf-timer-popup-open')) {
          document.body.classList.remove('sf-timer-popup-open');
        }
      };

      const releaseExternalScrollLock = () => {
        if (document.querySelector('cart-drawer.is-visible')) return;
        document.documentElement.style.removeProperty('overflow');
        if (document.body.style.overflow === 'hidden') document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
      };

      suppressTimerPopups();

      const hideExternalModal = (node) => {
        if (!(node instanceof Element)) return;
        const modal = node.matches('[role="dialog"]') ? node : node.querySelector?.('[role="dialog"]');
        if (!modal) return;
        const isKlaviyo = modal.querySelector('.klaviyo-form, form[data-testid^="klaviyo-form"]');
        if (!isKlaviyo) return;
        modal.hidden = true;
        modal.style.setProperty('display', 'none', 'important');
        window.setTimeout(releaseExternalScrollLock, 0);
        window.setTimeout(releaseExternalScrollLock, 100);
      };

      document.querySelectorAll('[role="dialog"]').forEach(hideExternalModal);
      this.popupObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach(hideExternalModal);

          if (
            mutation.type === 'attributes' &&
            (mutation.target === document.body || mutation.target.matches?.('[data-sf-timer-popup]'))
          ) {
            suppressTimerPopups();
          }
        });
      });
      this.popupObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    }

    bindEvents() {
      this.variantButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectVariant(Number(button.dataset.variantId)));
      });

      this.bundleButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectBundle(button));
      });

      this.mixSelects.forEach((select) => {
        select.addEventListener('change', () => {
          if (Number(select.dataset.slotIndex) > 1) select.dataset.userSelected = 'true';
          if (Number(select.dataset.slotIndex) === 1) this.selectVariant(Number(select.value), false);
          this.updateOffer();
        });
      });

      this.mediaButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectMedia(button));
      });

      this.addButton?.addEventListener('click', () => this.addToCart());
      this.stickyButton?.addEventListener('click', () => this.addToCart());
    }

    getVariant(id) {
      return this.variants.find((variant) => Number(variant.id) === Number(id));
    }

    selectVariant(variantId, syncFirstSelect = true) {
      const variant = this.getVariant(variantId);
      if (!variant?.available) return;

      this.selectedVariantId = Number(variant.id);
      this.variantButtons.forEach((button) => {
        const selected = Number(button.dataset.variantId) === this.selectedVariantId;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const colorLabel = this.root.querySelector('[data-sf-selected-color]');
      if (colorLabel) colorLabel.textContent = variant.title;

      if (syncFirstSelect) this.syncMixSelections(false);
      if (variant.image) this.updateMainImage(variant.image, variant.imageAlt || variant.title);
      this.updateOffer();
    }

    selectBundle(button) {
      this.quantity = Math.max(1, Math.min(4, Number(button.dataset.quantity || 1)));
      this.discount = Math.max(0, Number(button.dataset.discount || 0));
      this.bundleButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.updateMixPanel();
      this.syncMixSelections(false);
      this.updateOffer();
    }

    syncMixSelections(forceAll) {
      this.mixSelects.forEach((select) => {
        const slotIndex = Number(select.dataset.slotIndex || 1);
        if (forceAll || slotIndex === 1 || !select.dataset.userSelected) {
          const matching = Array.from(select.options).find(
            (option) => Number(option.value) === this.selectedVariantId && !option.disabled
          );
          if (matching) select.value = matching.value;
        }
      });
    }

    updateMixPanel() {
      if (this.mixPanel) this.mixPanel.hidden = this.quantity === 1;
      this.mixSlots.forEach((slot) => {
        slot.hidden = this.quantity === 1 || Number(slot.dataset.slotIndex) > this.quantity;
      });
    }

    selectedItems() {
      if (this.quantity === 1) return [{ id: this.selectedVariantId, quantity: 1 }];

      const counts = new Map();
      this.mixSelects.slice(0, this.quantity).forEach((select) => {
        const id = Number(select.value);
        const variant = this.getVariant(id);
        if (!variant?.available) return;
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
    }

    selectedRegularTotal(quantity = this.quantity) {
      if (quantity === this.quantity && quantity > 1) {
        const items = this.selectedItems();
        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
        if (itemCount === quantity) {
          return items.reduce((sum, item) => {
            const variant = this.getVariant(item.id);
            return sum + Number(variant?.price || 0) * item.quantity;
          }, 0);
        }
      }
      return Number(this.getVariant(this.selectedVariantId)?.price || this.root.dataset.unitPrice || 0) * quantity;
    }

    discountedTotal(regularTotal, discount) {
      return Math.round(regularTotal * (100 - discount) / 100);
    }

    updateOffer() {
      const variant = this.getVariant(this.selectedVariantId);
      const unitPrice = Number(variant?.price || this.root.dataset.unitPrice || 0);
      const unitPriceElement = this.root.querySelector('[data-sf-unit-price]');
      if (unitPriceElement) unitPriceElement.textContent = this.formatMoney(unitPrice);

      this.bundleButtons.forEach((button) => {
        const quantity = Number(button.dataset.quantity || 1);
        const discount = Number(button.dataset.discount || 0);
        const regularTotal = quantity === this.quantity
          ? this.selectedRegularTotal(quantity)
          : unitPrice * quantity;
        const total = this.discountedTotal(regularTotal, discount);
        const perItem = Math.round(total / quantity);
        const savings = regularTotal - total;
        const totalElement = button.querySelector('[data-sf-tier-total]');
        const unitElement = button.querySelector('[data-sf-tier-unit]');
        const saveElement = button.querySelector('[data-sf-tier-save]');
        if (totalElement) totalElement.textContent = this.formatMoney(total);
        if (unitElement) unitElement.textContent = `${this.formatMoney(perItem)}/ea`;
        if (saveElement) saveElement.textContent = `Save ${this.formatMoney(savings)} · ${discount}%`;
      });

      const activeRegularTotal = this.selectedRegularTotal();
      const activeTotal = this.discountedTotal(activeRegularTotal, this.discount);
      const addLabel = this.root.querySelector('[data-sf-add-label]');
      const addPrice = this.root.querySelector('[data-sf-add-price]');
      const stickyTitle = this.root.querySelector('[data-sf-sticky-title]');
      const stickyPrice = this.root.querySelector('[data-sf-sticky-price]');
      if (addLabel) addLabel.textContent = this.quantity === 1 ? this.baseCtaLabel : `Add ${this.quantity} to cart`;
      if (addPrice) addPrice.textContent = this.formatMoney(activeTotal);
      if (stickyTitle) stickyTitle.textContent = this.quantity === 1 ? variant?.title || '1 Splitflask' : `${this.quantity}-Pack`;
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(activeTotal);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      }).format(Number(cents || 0) / 100);
    }

    selectMedia(button) {
      const src = button.dataset.imageSrc;
      if (!src) return;
      this.updateMainImage(src, button.dataset.imageAlt || 'Splitflask dual-chamber tumbler');
      this.mediaButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateMainImage(src, alt) {
      if (!this.mainImage || !src) return;
      this.mainImage.style.opacity = '0.55';
      window.setTimeout(() => {
        this.mainImage.removeAttribute('srcset');
        this.mainImage.src = src;
        this.mainImage.alt = alt || 'Splitflask dual-chamber tumbler';
        this.mainImage.style.opacity = '1';
      }, 90);
      this.mediaButtons.forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });
    }

    setupStickyButton() {
      if (!this.sticky || !this.addButton || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver((entries) => {
        const addButtonVisible = entries[0]?.isIntersecting;
        this.sticky.classList.toggle('is-visible', !addButtonVisible);
        this.sticky.setAttribute('aria-hidden', addButtonVisible ? 'true' : 'false');
      }, { threshold: 0.35 });
      this.stickyObserver.observe(this.addButton);
    }

    async addToCart() {
      if (this.submitting) return;
      const items = this.selectedItems();
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      if (!items.length || itemCount !== this.quantity) {
        this.showStatus('Please choose an available color for every Splitflask.');
        return;
      }

      this.submitting = true;
      this.showStatus('');
      this.setLoading(true);
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      try {
        const body = { items };
        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.section);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'We could not add this selection. Please try again.');
        }

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'sf-paid-landing',
            productVariantId: this.selectedVariantId,
            cartData: payload,
          });
        }

        window.dispatchEvent(new CustomEvent('sf:paid-landing-add', {
          detail: { items, quantity: this.quantity, discount: this.discount },
        }));

        await this.refreshCartCount();
        if (cart?.renderContents) {
          cart.renderContents(payload);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } catch (error) {
        this.showStatus(error.message || 'We could not add this selection. Please try again.');
        console.error(error);
      } finally {
        this.setLoading(false);
        this.submitting = false;
      }
    }

    async refreshCartCount() {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cartData = await response.json();
        const badge = this.root.querySelector('[data-sf-paid-cart-count]');
        if (!badge) return;
        badge.textContent = cartData.item_count;
        badge.classList.toggle('is-empty', cartData.item_count === 0);
      } catch (error) {
        console.warn('Splitflask paid landing: cart count could not be refreshed.', error);
      }
    }

    setLoading(isLoading) {
      this.addButton?.classList.toggle('is-loading', isLoading);
      this.addButton?.toggleAttribute('disabled', isLoading);
      this.stickyButton?.toggleAttribute('disabled', isLoading);
    }

    showStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => new SplitflaskPaidLanding(root));
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
